import { type LinkProps, useRouterState } from "@tanstack/react-router";
import { ChevronRightIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Button as AriaButton,
  Dialog,
  Modal,
  ModalOverlay,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import type { SessionViewer } from "@/auth/shared";
import {
  isAdminRole,
  isSuperadminRole,
  roleHasPermission,
  useViewer,
} from "@/auth/shared";
import { Button } from "@/components/base/button";
import { Link } from "@/components/base/link";
import { NewsletterModal } from "@/components/newsletter-form";
import { useSideNav } from "@/components/side-nav-context";
import { useRulebookUrl, useStoreUrl } from "@/hooks/settings";

type SideNavLink = {
  label: string;
  visible?: (viewer: SessionViewer | undefined) => boolean;
  children?: SideNavLink[];
} & (
  | {
      to: LinkProps["to"];
      params?: LinkProps["params"];
      href?: never;
      action?: never;
    }
  | { href: string; to?: never; params?: never; action?: never }
  | { action: string; to?: never; params?: never; href?: never }
  | {
      to?: never;
      params?: never;
      href?: never;
      action?: never;
    }
);

type SideNavSection = {
  id: string;
  title: string;
  visible?: (viewer: SessionViewer | undefined) => boolean;
  links: SideNavLink[];
};

const sections: SideNavSection[] = [
  {
    id: "general",
    title: "General",
    links: [
      { to: "/tournaments", label: "Tournaments" },
      { to: "/juniors", label: "Juniors" },
      {
        to: "/faqs/{-$subject}",
        params: { subject: "parents" },
        label: "Parent's FAQ",
      },
      { to: "/search", label: "Search" },
      { to: "/ratings", label: "Ratings" },
      {
        to: "/leaderboard/{-$gender}",
        params: { gender: "womens" },
        label: "Leaderboard",
      },
      { to: "/faqs/{-$subject}", label: "FAQs" },
      { to: "/gallery", label: "Photos" },
      { to: "/info/cedars", label: "Injury Prevention" },
      { to: "/prize-pool", label: "Prize Pool" },
      {
        action: "newsletter",
        label: "Newsletter",
      },
    ],
  },
  {
    id: "account",
    title: "Account",
    visible: (v) => Boolean(v),
    links: [
      { to: "/account", label: "My Account" },
      { to: "/account/registrations", label: "Register" },
      { to: "/log-out", label: "Log Out" },
    ],
  },
  {
    id: "td",
    title: "Tournament Director",
    visible: (v) =>
      v ? roleHasPermission(v.role, { tournaments: ["update"] }) : false,
    links: [
      { to: "/td", label: "TD Dashboard" },
      { to: "/td/venues", label: "Venue Settings" },
      { to: "/td/players", label: "Player Outreach" },
      { to: "/td/help", label: "Help" },
    ],
  },
  {
    id: "admin",
    title: "Admin",
    visible: (v) => isAdminRole(v?.role),
    links: [
      { to: "/admin", label: "Dashboard" },
      { to: "/admin/profiles", label: "Profiles" },
      { to: "/admin/schedule", label: "Schedule" },
      { to: "/admin/settings", label: "Settings" },
      { to: "/admin/invoices", label: "Invoices" },
      { to: "/admin/venues", label: "Locations" },
      {
        label: "Reports",
        children: [
          { to: "/admin/reports/divisions", label: "Divisions" },
          { to: "/admin/reports/tshirts", label: "T-Shirts" },
        ],
      },
      {
        to: "/admin/reconcile-profiles",
        label: "Reconcile Profiles",
        visible: (v) => isSuperadminRole(v?.role),
      },
    ],
  },
];

const authLinks: SideNavLink[] = [
  { to: "/log-in", label: "Log In", visible: (v) => !v },
  { to: "/sign-up", label: "Sign Up", visible: (v) => !v },
];

const overlayStyles = tv({
  base: "fixed inset-0 isolate z-20 bg-black/[15%] backdrop-blur-lg",
  variants: {
    isEntering: {
      true: "animate-in fade-in duration-200 ease-out",
    },
    isExiting: {
      true: "animate-out fade-out duration-200 ease-in",
    },
  },
});

const drawerStyles = tv({
  base: "fixed left-0 top-0 bottom-0 w-72 bg-navbar-background shadow-2xl border-r border-navbar-border outline-none",
  variants: {
    isEntering: {
      true: "animate-in slide-in-from-left duration-200 ease-out",
    },
    isExiting: {
      true: "animate-out slide-out-to-left duration-200 ease-in",
    },
  },
});

const navLinkStyles = tv({
  base: "block px-4 py-2 text-sm no-underline rounded-md transition-colors text-navbar-foreground",
  variants: {
    isActive: {
      true: "bg-navbar-foreground text-navbar-foreground-hover font-semibold",
      false: "hover:bg-navbar-foreground hover:text-navbar-foreground-hover",
    },
  },
  defaultVariants: {
    isActive: false,
  },
});

const sectionHeaderStyles = tv({
  base: "flex items-center justify-between w-full px-4 py-2 text-sm font-semibold uppercase tracking-wider text-navbar-foreground cursor-pointer hover:bg-navbar-foreground hover:text-navbar-foreground-hover rounded-md transition-colors",
});

const chevronStyles = tv({
  base: "w-3 h-3 transition-transform duration-200",
  variants: {
    isExpanded: {
      true: "rotate-90",
    },
  },
});

function getDefaultExpanded(pathname: string): string[] {
  const expanded: string[] = ["general"];
  if (pathname.startsWith("/admin")) expanded.push("admin");
  if (pathname.startsWith("/td")) expanded.push("td");
  if (pathname.startsWith("/account")) expanded.push("account");
  return expanded;
}

function getDefaultExpandedSubmenus(pathname: string): string[] {
  const expanded: string[] = [];
  if (pathname.startsWith("/admin/reports")) expanded.push("Reports");
  return expanded;
}

const submenuToggleStyles = tv({
  base: "flex items-center justify-between w-full py-2 text-sm no-underline rounded-md transition-colors text-navbar-foreground hover:bg-navbar-foreground hover:text-navbar-foreground-hover text-left",
});

function indentStyle(indent?: boolean | "deep") {
  if (indent === "deep") return { paddingLeft: "3rem" };
  if (indent) return { paddingLeft: "2rem" };
  return undefined;
}

function NavLink({
  label,
  indent,
  onAction,
  children,
  expandedSubmenus,
  onToggleSubmenu,
  ...rest
}: SideNavLink & {
  indent?: boolean | "deep";
  onAction?: (key: string) => void;
  expandedSubmenus?: Set<string>;
  onToggleSubmenu?: (label: string) => void;
}) {
  if (children && children.length > 0) {
    const isExpanded = expandedSubmenus?.has(label) ?? false;
    return (
      <div>
        <AriaButton
          onPress={() => onToggleSubmenu?.(label)}
          className={submenuToggleStyles()}
          style={{
            paddingLeft: indent ? "2rem" : "1rem",
            paddingRight: "1rem",
          }}
        >
          {label}
          <ChevronRightIcon className={chevronStyles({ isExpanded })} />
        </AriaButton>
        {isExpanded && (
          <div className="flex flex-col gap-0.5">
            {children.map((child) => (
              <NavLink
                key={child.action ?? child.href ?? String(child.to)}
                {...child}
                indent="deep"
                onAction={onAction}
                expandedSubmenus={expandedSubmenus}
                onToggleSubmenu={onToggleSubmenu}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (rest.action) {
    return (
      <AriaButton
        aria-label={label}
        onPress={() => onAction?.(rest.action)}
        className={`${navLinkStyles({ isActive: false })} text-left`}
        style={indentStyle(indent)}
      >
        {label}
      </AriaButton>
    );
  }

  if (rest.href) {
    return (
      <a
        href={rest.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        className={navLinkStyles({ isActive: false })}
        style={indentStyle(indent)}
      >
        {label}
      </a>
    );
  }

  if (!rest.to) return null;

  return (
    <Link
      to={rest.to}
      params={rest.params}
      aria-label={label}
      activeOptions={{ exact: true }}
      activeProps={{ className: navLinkStyles({ isActive: true }) }}
      inactiveProps={{ className: navLinkStyles({ isActive: false }) }}
      style={indentStyle(indent)}
    >
      {label}
    </Link>
  );
}

function SectionGroup({
  id,
  title,
  links,
  expanded,
  onToggle,
  onAction,
  expandedSubmenus,
  onToggleSubmenu,
}: {
  id: string;
  title: string;
  links: SideNavLink[];
  expanded: boolean;
  onToggle: (id: string) => void;
  onAction?: (key: string) => void;
  expandedSubmenus: Set<string>;
  onToggleSubmenu: (label: string) => void;
}) {
  return (
    <div>
      <AriaButton
        onPress={() => onToggle(id)}
        className={sectionHeaderStyles()}
      >
        {title}
        <ChevronRightIcon className={chevronStyles({ isExpanded: expanded })} />
      </AriaButton>
      {expanded && (
        <div className="flex flex-col gap-0.5 px-2">
          {links.map((link) => (
            <NavLink
              key={link.action ?? link.href ?? link.label}
              {...link}
              indent
              onAction={onAction}
              expandedSubmenus={expandedSubmenus}
              onToggleSubmenu={onToggleSubmenu}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function SideNav() {
  const { isOpen, setIsOpen } = useSideNav();
  const viewer = useViewer();
  const { location } = useRouterState();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(getDefaultExpanded(location.pathname)),
  );
  const [expandedSubmenus, setExpandedSubmenus] = useState<Set<string>>(
    () => new Set(getDefaultExpandedSubmenus(location.pathname)),
  );
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const storeUrl = useStoreUrl();
  const rulebookUrl = useRulebookUrl();

  function handleAction(key: string) {
    if (key === "newsletter") {
      setIsOpen(false);
      setNewsletterOpen(true);
    }
  }

  // Close drawer and reset submenu state on navigation
  // biome-ignore lint/correctness/useExhaustiveDependencies: location.pathname is intentionally used as a trigger
  useEffect(() => {
    setIsOpen(false);
    setExpandedSubmenus(new Set(getDefaultExpandedSubmenus(location.pathname)));
  }, [location.pathname, setIsOpen]);

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSubmenu = (label: string) => {
    setExpandedSubmenus((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const resolvedSections = useMemo(() => {
    return sections.map((section) => {
      if (section.id === "general") {
        const links = [...section.links];
        // Rulebook keeps its original slot before the Newsletter action.
        if (rulebookUrl) {
          const newsletterIdx = links.findIndex(
            (l) => l.action === "newsletter",
          );
          const at = newsletterIdx === -1 ? links.length : newsletterIdx;
          links.splice(at, 0, {
            href: rulebookUrl,
            label: "Rulebook",
          } as SideNavLink);
        }
        if (storeUrl) {
          links.push({ href: storeUrl, label: "Store" } as SideNavLink);
        }
        return { ...section, links };
      }
      return section;
    });
  }, [storeUrl, rulebookUrl]);

  const visibleSections = resolvedSections.filter(
    (s) => !s.visible || s.visible(viewer ?? undefined),
  );

  const visibleAuthLinks = authLinks.filter(
    (l) => !l.visible || l.visible(viewer ?? undefined),
  );

  return (
    <>
      <ModalOverlay
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        isDismissable
        className={overlayStyles}
      >
        <Modal isDismissable className={drawerStyles}>
          <Dialog
            aria-label="Navigation"
            className="text-navbar-foreground flex h-full flex-col outline-none"
          >
            <div className="border-navbar-border flex items-center justify-between border-b p-4">
              <img alt="CBVA Logo" className="h-8" src="/logos/cbva.svg" />
              <Button
                aria-label="Close navigation"
                variant="icon"
                slot="close"
                color="secondary"
              >
                <XIcon size={16} />
              </Button>
            </div>

            <nav className="flex-1 overflow-y-auto py-3">
              <div className="flex flex-col gap-0.5 px-2">
                {visibleSections.map((section) => {
                  const visibleLinks = section.links.filter(
                    (l) => !l.visible || l.visible(viewer ?? undefined),
                  );
                  if (visibleLinks.length === 0) return null;

                  return (
                    <SectionGroup
                      key={section.id}
                      id={section.id}
                      title={section.title}
                      links={visibleLinks}
                      expanded={expandedSections.has(section.id)}
                      onToggle={toggleSection}
                      onAction={handleAction}
                      expandedSubmenus={expandedSubmenus}
                      onToggleSubmenu={toggleSubmenu}
                    />
                  );
                })}
              </div>

              {visibleAuthLinks.length > 0 && (
                <div className="border-navbar-border mt-3 flex flex-col gap-0.5 border-t px-2 pt-3">
                  {visibleAuthLinks.map((link) => (
                    <NavLink key={link.href ?? String(link.to)} {...link} />
                  ))}
                </div>
              )}
            </nav>
          </Dialog>
        </Modal>
      </ModalOverlay>
      <NewsletterModal
        isOpen={newsletterOpen}
        onOpenChange={setNewsletterOpen}
      />
    </>
  );
}
